<template>
  <section
    class="rounded-xl border border-gray-200 bg-white p-4"
    aria-labelledby="school-subject-analysis-title">
    <div class="flex flex-wrap items-start justify-between gap-2">
      <div>
        <h4
          id="school-subject-analysis-title"
          class="text-sm font-black text-gray-900">
          分野別分析
        </h4>
        <p class="mt-1 text-xs font-medium text-gray-500">
          {{ subjectAnalysisDescription }}
        </p>
      </div>
      <div class="flex flex-col gap-1 text-[11px] font-bold text-gray-500">
        <span class="inline-flex items-center gap-1">
          <i class="h-2 w-4 rounded-sm border-2 border-green-600"></i>
          校内正答率（％）
        </span>
        <span
          v-if="nationalAnalyticsReady"
          class="inline-flex items-center gap-1">
          <i class="h-2 w-4 rounded-sm border-2 border-gray-400"></i>
          全国平均（％）
        </span>
      </div>
    </div>

    <div class="mt-0 flex flex-col gap-3 md:mt-4 md:flex-row md:items-start md:gap-4 md:pr-4 lg:pr-6">
      <div class="min-w-0 overflow-hidden md:w-[69%] md:flex-none">
        <div
          class="relative h-60 cursor-pointer overflow-hidden sm:h-72 md:h-[360px]"
          @click="handleSubjectChartClick">
          <RadarChart
            ref="subjectChartRef"
            css-classes="h-full w-full"
            :chart-data="subjectChartData"
            :options="subjectRadarOptions" />
        </div>
        <div class="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] font-bold text-gray-500 md:mt-5">
          <span class="inline-flex items-center gap-1">
            <i class="h-2 w-4 rounded-sm border-2 border-green-600"></i>
            校内正答率（％）
          </span>
          <span
            v-if="nationalAnalyticsReady"
            class="inline-flex items-center gap-1">
            <i class="h-2 w-4 rounded-sm border-2 border-gray-400"></i>
            全国平均（％）
          </span>
        </div>
      </div>

      <div class="relative z-10 grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:w-[31%] md:flex-none md:grid-cols-1">
        <button
          v-for="subject in subjectBreakdownLinks"
          :key="subject.id"
          type="button"
          :disabled="!subject.hasBreakdown"
          class="flex min-h-[64px] items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition disabled:cursor-not-allowed disabled:opacity-60"
          :class="selectedSubjectId === subject.id
            ? 'border-blue-500 bg-blue-50 text-blue-800'
            : subject.hasBreakdown
              ? 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:text-blue-700'
              : 'border-gray-200 bg-gray-50 text-gray-500'"
          @click="openSubjectModal(subject.id)">
          <span class="min-w-0 flex-1">
            <strong class="block truncate text-sm font-bold leading-snug text-gray-900">
              {{ subject.label }}
            </strong>
            <small class="mt-1 block whitespace-nowrap text-xs font-semibold text-gray-500">
              <b class="text-green-700">校内 {{ subject.schoolRate }}%</b>
              <template v-if="nationalAnalyticsReady">
                <span class="mx-1">/</span>
                全国 {{ subject.nationalRate }}%
              </template>
            </small>
          </span>
          <span class="flex h-8 w-5 shrink-0 items-center justify-center text-gray-400">
            <svg
              class="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </button>
      </div>
    </div>

    <DialogModal
      :show="isSubjectModalOpen"
      max-width="2xl"
      show-scroll-hint
      @close="closeSubjectModal">
      <template #title>
        <div class="flex items-center justify-between gap-3">
          <div>
            <div class="text-lg font-semibold text-gray-900">
              {{ selectedSubjectLabel }} の分野別成績
            </div>
            <div class="mt-1 text-sm text-gray-500">
              {{ subjectModalDescription }}
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <div
          v-if="selectedSubjectId"
          class="space-y-6">
          <!-- 4項目は2列、3項目は横並びにして分野別サマリーの高さを揃える -->
          <div
            class="grid gap-4"
            :class="nationalAnalyticsReady ? 'grid-cols-2' : 'grid-cols-3'">
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-gray-700 sm:text-xs">校内平均</div>
              <div class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                {{ selectedSubjectSchoolRate }}%
              </div>
            </div>
            <div
              v-if="nationalAnalyticsReady"
              class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-gray-200 bg-gray-100 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-gray-600 sm:text-xs">全国平均</div>
              <div class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                {{ selectedSubjectNationalRate }}%
              </div>
            </div>
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-green-100 bg-green-50 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-green-600 sm:text-xs">強い分野</div>
              <div class="space-y-1">
                <div class="break-words text-sm font-semibold leading-5 text-green-900 sm:text-base">
                  {{ strongestField?.name || '-' }}
                </div>
                <div class="text-2xl font-bold tracking-tight text-green-900 sm:text-3xl">
                  {{ strongestField?.schoolRate ?? '-' }}%
                </div>
              </div>
            </div>
            <div class="flex min-h-[94px] flex-col items-center justify-between rounded-xl border border-red-100 bg-red-50 px-3 py-3 text-center shadow-sm sm:px-4 sm:py-4">
              <div class="text-[11px] font-semibold leading-4 text-red-600 sm:text-xs">弱い分野</div>
              <div class="space-y-1">
                <div class="break-words text-sm font-semibold leading-5 text-red-900 sm:text-base">
                  {{ weakestField?.name || '-' }}
                </div>
                <div class="text-2xl font-bold tracking-tight text-red-900 sm:text-3xl">
                  {{ weakestField?.schoolRate ?? '-' }}%
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <div class="h-80">
              <BarChart
                :key="selectedSubjectId"
                :chart-data="selectedFieldChartData"
                css-classes="h-full w-full"
                :options="modalBarOptions" />
            </div>
          </div>

          <div class="overflow-x-auto rounded-2xl border border-gray-200">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    分野
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    問題数
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    校内
                  </th>
                  <th
                    v-if="nationalAnalyticsReady"
                    class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    全国
                  </th>
                  <th
                    v-if="nationalAnalyticsReady"
                    class="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    差分
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 bg-white">
                <tr
                  v-for="field in selectedFieldBreakdown"
                  :key="field.name">
                  <td class="px-4 py-3 text-sm font-medium text-gray-900">
                    {{ field.name }}
                  </td>
                  <td class="px-4 py-3 text-right text-sm text-gray-700">
                    {{ field.questionCount }}問
                  </td>
                  <td class="px-4 py-3 text-right text-sm text-green-700">
                    {{ field.schoolRate }}%
                  </td>
                  <td
                    v-if="nationalAnalyticsReady"
                    class="px-4 py-3 text-right text-sm text-gray-700">
                    {{ field.nationalRate }}%
                  </td>
                  <td
                    v-if="nationalAnalyticsReady"
                    class="px-4 py-3 text-right text-sm font-medium"
                    :class="getGapClass(field.schoolRate - field.nationalRate)">
                    {{ formatGap(field.schoolRate - field.nationalRate) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          type="button"
          class="rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-700"
          @click="closeSubjectModal">
          閉じる
        </button>
      </template>
    </DialogModal>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Chart, registerables } from 'chart.js'
import { BarChart, RadarChart } from 'vue-chart-3'
import DialogModal from '@/Common/DialogModal'

Chart.register(...registerables)

const props = defineProps({
  nationalAnalyticsReady: {
    type: Boolean,
    default: false,
  },
  subjectNames: {
    type: Array,
    default: () => [],
  },
  questionCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  subjectCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  schoolSubjectCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  schoolQuestionCorrentAnswerRate: {
    type: Array,
    default: () => [],
  },
  subjectFieldBreakdowns: {
    type: Object,
    default: () => ({}),
  },
})

const subjectBreakdownLabels = ['基礎動物学', '基礎動物看護学', '臨床動物看護学', '愛護・適正飼養学', '実習']

const subjectNameMap = computed(() => new Map(props.subjectNames.map(item => [item.id, item.name])))

const subjectAnalysisDescription = computed(() => (props.nationalAnalyticsReady
  ? 'レーダーチャートまたは科目ボタンから、校内と全国の分野別内訳を確認できます。'
  : 'レーダーチャートまたは科目ボタンから、校内の分野別内訳を確認できます。'))

const subjectModalDescription = computed(() => (props.nationalAnalyticsReady
  ? '分野別の校内・全国正答率を表示します。'
  : '分野別の校内正答率を表示します。'))

const getSubjectLabel = (subjectId) => subjectNameMap.value.get(subjectId) || subjectBreakdownLabels[subjectId - 1] || '-'

const getSubjectRate = (rates, subjectId) => {
  const rate = rates[subjectId - 1]?.correct_answer_rate

  return Number.isFinite(rate) ? rate : 0
}

const getSubjectFieldBreakdown = (subjectId) => props.subjectFieldBreakdowns[subjectId] || []

const subjectChartRef = ref(null)
const selectedSubjectId = ref(null)

const subjectChartData = computed(() => {
  const datasets = [
    {
      label: '校内：科目ごとの正答率(%)',
      data: [
        getSubjectRate(props.schoolSubjectCorrentAnswerRate, 1),
        getSubjectRate(props.schoolSubjectCorrentAnswerRate, 2),
        getSubjectRate(props.schoolSubjectCorrentAnswerRate, 3),
        getSubjectRate(props.schoolSubjectCorrentAnswerRate, 4),
        getSubjectRate(props.schoolSubjectCorrentAnswerRate, 5),
      ],
      backgroundColor: 'rgba(46, 204, 113, 0.18)',
      borderColor: '#16A34A',
      pointBackgroundColor: '#16A34A',
      pointBorderColor: '#FFFFFF',
      pointHoverBackgroundColor: '#16A34A',
      pointHoverBorderColor: '#FFFFFF',
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    },
  ]

  if (props.nationalAnalyticsReady) {
    datasets.push({
      label: '全国：科目ごとの正答率(%)',
      data: [
        getSubjectRate(props.subjectCorrentAnswerRate, 1),
        getSubjectRate(props.subjectCorrentAnswerRate, 2),
        getSubjectRate(props.subjectCorrentAnswerRate, 3),
        getSubjectRate(props.subjectCorrentAnswerRate, 4),
        getSubjectRate(props.subjectCorrentAnswerRate, 5),
      ],
      backgroundColor: 'rgba(107, 114, 128, 0.14)',
      borderColor: '#9CA3AF',
      pointBackgroundColor: '#9CA3AF',
      pointBorderColor: '#FFFFFF',
      pointHoverBackgroundColor: '#9CA3AF',
      pointHoverBorderColor: '#FFFFFF',
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    })
  }

  return {
    labels: subjectBreakdownLabels,
    datasets,
  }
})

const subjectRadarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'nearest',
    intersect: false,
  },
  onHover: (_event, elements, chart) => {
    const { canvas } = chart

    canvas.style.cursor = elements.length ? 'pointer' : 'default'
  },
  plugins: {
    legend: {
      display: false,
    },
  },
  elements: {
    point: {
      hitRadius: 18,
    },
  },
  scales: {
    r: {
      beginAtZero: true,
      min: 0,
      max: 100,
      ticks: {
        stepSize: 20,
        callback: (value) => `${value}%`,
        backdropColor: 'transparent',
      },
      pointLabels: {
        font: {
          size: 10,
        },
      },
    },
  },
}

const modalBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 700,
    easing: 'easeOutQuart',
  },
  animations: {
    x: {
      duration: 0,
    },
    base: {
      from: (context) => context.chart.scales.y.getBasePixel(),
    },
    y: {
      from: (context) => context.chart.scales.y.getBasePixel(),
    },
  },
  plugins: {
    legend: {
      position: 'bottom',
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        callback: (value) => `${value}%`,
      },
    },
  },
}

const subjectBreakdownLinks = computed(() => subjectBreakdownLabels.map((label, index) => {
  const subjectId = index + 1

  return {
    id: subjectId,
    label,
    schoolRate: getSubjectRate(props.schoolSubjectCorrentAnswerRate, subjectId),
    nationalRate: getSubjectRate(props.subjectCorrentAnswerRate, subjectId),
    hasBreakdown: getSubjectFieldBreakdown(subjectId).length > 0,
  }
}))

const isSubjectModalOpen = computed(() => !!selectedSubjectId.value)

const selectedFieldBreakdown = computed(() => getSubjectFieldBreakdown(selectedSubjectId.value))

const selectedSubjectLabel = computed(() => {
  if (!selectedSubjectId.value) {
    return ''
  }

  return getSubjectLabel(selectedSubjectId.value)
})

const selectedSubjectSchoolRate = computed(() => {
  if (!selectedSubjectId.value) {
    return 0
  }

  return getSubjectRate(props.schoolSubjectCorrentAnswerRate, selectedSubjectId.value)
})

const selectedSubjectNationalRate = computed(() => {
  if (!selectedSubjectId.value || !props.nationalAnalyticsReady) {
    return null
  }

  return getSubjectRate(props.subjectCorrentAnswerRate, selectedSubjectId.value)
})

const strongestField = computed(() => {
  if (!selectedFieldBreakdown.value.length) {
    return null
  }

  return [...selectedFieldBreakdown.value].sort((left, right) => right.schoolRate - left.schoolRate)[0]
})

const weakestField = computed(() => {
  if (!selectedFieldBreakdown.value.length) {
    return null
  }

  // 校内正答率が最も低い分野を「弱い分野」として表示する。
  return [...selectedFieldBreakdown.value].sort((left, right) => left.schoolRate - right.schoolRate)[0]
})

const selectedFieldChartData = computed(() => {
  const datasets = [
    {
      label: '校内正答率',
      data: selectedFieldBreakdown.value.map((field) => field.schoolRate),
      backgroundColor: '#09A963',
      borderRadius: 8,
    },
  ]

  if (props.nationalAnalyticsReady) {
    datasets.push({
      label: '全国正答率',
      data: selectedFieldBreakdown.value.map((field) => field.nationalRate),
      backgroundColor: '#9CA3AF',
      borderRadius: 8,
    })
  }

  return {
    labels: selectedFieldBreakdown.value.map((field) => field.name),
    datasets,
  }
})

const openSubjectModal = (subjectId) => {
  if (!getSubjectFieldBreakdown(subjectId).length) {
    return
  }

  selectedSubjectId.value = subjectId
}

const closeSubjectModal = () => {
  selectedSubjectId.value = null
}

const handleSubjectChartClick = (event) => {
  const chart = subjectChartRef.value?.chartInstance

  if (!chart) {
    return
  }

  const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true)

  if (!elements.length) {
    return
  }

  openSubjectModal(elements[0].index + 1)
}

const formatGap = (gap) => `${gap > 0 ? '+' : ''}${gap}%`

const getGapClass = (gap) => {
  if (gap > 0) {
    return 'text-emerald-600'
  }

  if (gap < 0) {
    return 'text-rose-600'
  }

  return 'text-gray-500'
}
</script>
